import {
  computed,
  ref,
  toValue,
  watch,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue'

import {
  resolveClient,
  type ApolloClientLike,
} from '@/shared/ports/resolveClient'
import {
  GuildDiscordReferencesDocument,
  type GuildDiscordReferenceBotAccountStatusWire,
  type GuildDiscordReferenceWire,
  type GuildDiscordReferencesQuery,
  type GuildDiscordReferencesQueryVariables,
} from '../lib/handWrittenGraphql'
import { isDiscordSnowflake } from '../lib/notificationSettings'

export type DiscordReferenceKind = 'ROLE' | 'CHANNEL'
export type ForeignGuildBotAccountRole = 'coordinator' | 'worker'

export interface ForeignGuildBotAccountInvite {
  readonly index: number
  readonly role: ForeignGuildBotAccountRole
  readonly inviteUrl: string | null
  readonly requiresOauthCodeGrant: boolean
}

export interface GuildDiscordReference {
  readonly kind: DiscordReferenceKind
  readonly id: string
  readonly name: string | null
  readonly issue: string | null
  readonly foreignGuildBotAccountInvites: ReadonlyArray<ForeignGuildBotAccountInvite>
}

export interface UseGuildDiscordReferencesOptions {
  readonly guildId: MaybeRefOrGetter<string>
  readonly roleIds: MaybeRefOrGetter<ReadonlyArray<string>>
  readonly channelIds: MaybeRefOrGetter<ReadonlyArray<string>>
}

export interface GuildDiscordReferencesApi {
  readonly loading: Ref<boolean>
  readonly error: Ref<string | null>
  readonly reference: (
    kind: DiscordReferenceKind,
    id: string,
  ) => GuildDiscordReference | null
  readonly refresh: () => Promise<void>
}

interface LookupRequest {
  readonly guildId: string
  readonly roleIds: ReadonlyArray<string>
  readonly channelIds: ReadonlyArray<string>
}

function normalizedIds(ids: ReadonlyArray<string>): ReadonlyArray<string> {
  const values = ids
    .map((id) => id.trim())
    .filter(isDiscordSnowflake)
  return [...new Set(values)]
}

function key(kind: DiscordReferenceKind, id: string): string {
  return `${kind}:${id}`
}

function foreignGuildBotAccountInviteFromWire(
  value: GuildDiscordReferenceBotAccountStatusWire,
): ForeignGuildBotAccountInvite | null {
  if (value.role !== 'coordinator' && value.role !== 'worker') return null
  return {
    index: value.index,
    role: value.role,
    inviteUrl: value.inviteUrl,
    requiresOauthCodeGrant: value.requiresOauthCodeGrant,
  }
}

function referenceFromWire(value: GuildDiscordReferenceWire): GuildDiscordReference | null {
  if (value.kind !== 'ROLE' && value.kind !== 'CHANNEL') return null
  return {
    kind: value.kind,
    id: value.id,
    name: value.name,
    issue: value.issue,
    foreignGuildBotAccountInvites: (value.foreignGuildBotAccountStatuses ?? [])
      .map(foreignGuildBotAccountInviteFromWire)
      .filter((invite): invite is ForeignGuildBotAccountInvite => invite !== null),
  }
}

export function useGuildDiscordReferences(
  options: UseGuildDiscordReferencesOptions,
  apolloClient: ApolloClientLike = resolveClient(),
): GuildDiscordReferencesApi {
  const references = ref<ReadonlyMap<string, GuildDiscordReference>>(new Map())
  const loading = ref<boolean>(false)
  const error = ref<string | null>(null)
  let generation = 0
  let referencesGuildId: string | null = null

  const request = computed<LookupRequest>(() => ({
    guildId: toValue(options.guildId).trim(),
    roleIds: normalizedIds(toValue(options.roleIds)),
    channelIds: normalizedIds(toValue(options.channelIds)),
  }))

  watch(request, (next) => {
    void load(next)
  }, { immediate: true })

  async function load(next: LookupRequest): Promise<void> {
    const requestGeneration = ++generation
    if (referencesGuildId !== next.guildId) {
      references.value = new Map()
      referencesGuildId = null
    }
    error.value = null
    if (
      !isDiscordSnowflake(next.guildId)
      || (next.roleIds.length === 0 && next.channelIds.length === 0)
    ) {
      references.value = new Map()
      referencesGuildId = null
      loading.value = false
      return
    }

    loading.value = true
    try {
      const variables: GuildDiscordReferencesQueryVariables = next
      const result = await apolloClient.query<GuildDiscordReferencesQuery>({
        query: GuildDiscordReferencesDocument,
        variables: variables as never,
        fetchPolicy: 'no-cache',
      })
      if (requestGeneration !== generation) return
      const nextReferences = new Map<string, GuildDiscordReference>()
      for (const value of result.data?.guildDiscordReferences ?? []) {
        const reference = referenceFromWire(value)
        if (reference !== null) {
          nextReferences.set(key(reference.kind, reference.id), reference)
        }
      }
      references.value = nextReferences
      referencesGuildId = next.guildId
    } catch (cause: unknown) {
      if (requestGeneration !== generation) return
      error.value = cause instanceof Error ? cause.message : String(cause)
    } finally {
      if (requestGeneration === generation) loading.value = false
    }
  }

  function reference(
    kind: DiscordReferenceKind,
    id: string,
  ): GuildDiscordReference | null {
    return references.value.get(key(kind, id.trim())) ?? null
  }

  async function refresh(): Promise<void> {
    await load(request.value)
  }

  return { loading, error, reference, refresh }
}
